import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../supabaseClient';
import { logAudit } from '../../../utils/audit';
import { applyTeamName } from '../../../utils/teamNames';

export default function TeamForm({ team, onClose, onSave }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isCreateMode = !team;

  useEffect(() => {
    if (team) {
      setName(team.team_name || '');
    }
  }, [team]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!name.trim()) {
        setError('Team name is required');
        setLoading(false);
        return;
      }

      if (isCreateMode) {
        const { data: inserted, error: insertError } = await supabase
          .from('teams')
          .insert({ name: name.trim() })
          .select('id')
          .single();

        if (insertError) throw insertError;

        await logAudit({
          action: 'team.created',
          entityType: 'team',
          entityId: inserted?.id,
          entityLabel: name.trim(),
          changes: { name: name.trim() },
        });
      } else {
        // Renaming the team here renames it from its most recent year onward;
        // earlier years are pinned to the name they played under, so past
        // leaderboards and tournament history keep saying what actually happened.
        const result = await applyTeamName({ teamsId: team.team_id, newName: name.trim() });

        if ((team.team_name || '') !== name.trim()) {
          await logAudit({
            action: 'team.updated',
            entityType: 'team',
            entityId: team.team_id,
            entityLabel: name.trim(),
            changes: { name: { from: team.team_name || null, to: name.trim() } },
            metadata: result.frozenYears.length
              ? { years_pinned_to_previous_name: result.frozenYears }
              : undefined,
          });
        }
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving team:', err);
      setError(err.message || 'Failed to save team');
    } finally {
      setLoading(false);
    }
  };

  // Portal: keeps `fixed inset-0` clear of the caller's space-y-* sibling margin.
  return createPortal(
    <div className="admin-content fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start sm:items-center justify-center p-4 overflow-y-auto z-50">
      <div className="bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-md w-full modal-panel overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-night-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {isCreateMode ? 'New Team' : 'Edit Team'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Use Leaderboard Management to add a team to a tournament and enter scores.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Team Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="e.g., The Eagles, Dream Team"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {isCreateMode
                ? 'What this team is currently known as. Each year can play under its own name, set in Team Builder.'
                : 'Renaming applies from the team’s most recent tournament onward. Earlier years keep the name they played under, so past leaderboards and history are unchanged.'}
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-night-800 border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:bg-night-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (isCreateMode ? 'Create Team' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
